import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { moviesApi } from "@/api/movies";
import type { Movie, MovieLookupResult } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, SearchInput, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

/** Migration 0062: which slice of the library this page is currently
 *  showing. Drives both the moviesApi query param and the visual tab
 *  state. "public" is the default Library; "private" is the dedicated
 *  shelf the household media server can't see. */
type Tab = "public" | "private";

export function MovieList() {
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState<Tab>("public");
  const [filter, setFilter] = useState("");
  // Multi-select genre / cert chip filters (issue #52). Plain `Set` so
  // toggling is O(1) and the empty state ("no chips selected") cleanly
  // means "don't filter on this dimension".
  const [genreFilters, setGenreFilters] = useState<Set<string>>(
    () => new Set(),
  );
  const [certFilters, setCertFilters] = useState<Set<string>>(
    () => new Set(),
  );

  // Cache key includes the tab so flipping public ↔ private hits the
  // right server-side filter without bleeding rows from the other shelf.
  const { data: movies, isLoading } = useQuery({
    queryKey: ["movies", tab],
    queryFn: () => moviesApi.list(tab),
  });

  // Collect the universe of genres + certs across the loaded library.
  // Sorted alphabetically for stable rendering.
  const { allGenres, allCerts } = useMemo(() => {
    const g = new Set<string>();
    const c = new Set<string>();
    for (const m of movies ?? []) {
      for (const name of m.genres ?? []) g.add(name);
      if (m.certification) c.add(m.certification);
    }
    return {
      allGenres: [...g].sort((a, b) => a.localeCompare(b)),
      allCerts: [...c].sort((a, b) => a.localeCompare(b)),
    };
  }, [movies]);

  const lowerFilter = filter.trim().toLowerCase();
  const filteredMovies = useMemo(() => {
    if (!movies) return [];
    const sorted = [...movies].sort((a, b) =>
      a.sort_title.localeCompare(b.sort_title),
    );
    return sorted.filter((m) => {
      if (lowerFilter) {
        const hit =
          m.title.toLowerCase().includes(lowerFilter) ||
          String(m.year ?? "").includes(lowerFilter);
        if (!hit) return false;
      }
      if (genreFilters.size > 0) {
        // OR within the genre dimension — a movie tagged Action *or*
        // Drama matches when both chips are selected.
        const hasGenre = (m.genres ?? []).some((g) => genreFilters.has(g));
        if (!hasGenre) return false;
      }
      if (certFilters.size > 0) {
        if (!m.certification || !certFilters.has(m.certification)) return false;
      }
      return true;
    });
  }, [movies, lowerFilter, genreFilters, certFilters]);

  const filtersActive =
    !!filter || genreFilters.size > 0 || certFilters.size > 0;
  const summary = movies && (
    <span className="font-mono text-xs text-foreground/55">
      {filtersActive
        ? `${filteredMovies.length} of ${movies.length}`
        : movies.length}
    </span>
  );

  const toggleSetItem = (
    setState: React.Dispatch<React.SetStateAction<Set<string>>>,
    value: string,
  ) => {
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const clearAllFilters = () => {
    setFilter("");
    setGenreFilters(new Set());
    setCertFilters(new Set());
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Movies"
        title={tab === "private" ? "Private" : "Library"}
        description={
          tab === "private"
            ? "Hidden from the default Library view and the household media server. Tap a card to manage."
            : "Films you're monitoring. Tap one to manage releases."
        }
        actions={<Button onClick={() => setAdding(true)}>Add movie</Button>}
      />

      {/* Tab strip — migration 0062. Two shelves; Private is hidden from
          the household media server because its container isn't mounted
          at the private library root. */}
      <TabStrip tab={tab} onChange={setTab} />

      <div className="flex items-center justify-between gap-4">
        <div className="w-full max-w-sm">
          <SearchInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by title or year"
          />
        </div>
        {summary}
      </div>

      {(allGenres.length > 0 || allCerts.length > 0) && (
        <FilterStrip
          allGenres={allGenres}
          allCerts={allCerts}
          genreFilters={genreFilters}
          certFilters={certFilters}
          onToggleGenre={(g) => toggleSetItem(setGenreFilters, g)}
          onToggleCert={(c) => toggleSetItem(setCertFilters, c)}
        />
      )}

      {isLoading ? (
        <Skeleton.Cards count={10} cols={5} />
      ) : !movies || movies.length === 0 ? (
        <EmptyState
          glyph="film"
          title="No movies yet"
          description="Search TMDB and add your first film to start monitoring releases."
          action={<Button onClick={() => setAdding(true)}>Add movie</Button>}
        />
      ) : filteredMovies.length === 0 ? (
        <EmptyState
          glyph="search"
          title="No matches"
          description="Nothing in your library matches the current filters."
          action={
            <Button variant="ghost" onClick={clearAllFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {filteredMovies.map((m) => (
            <MovieCard key={m.id} movie={m} />
          ))}
        </div>
      )}

      <AddMovieModal
        open={adding}
        onClose={() => setAdding(false)}
        defaultPrivate={tab === "private"}
      />
    </div>
  );
}

/**
 * Two-tab strip — Library / Private (migration 0062). Visual treatment
 * mirrors the rest of the system's filter chips: active gets the accent
 * tone + a soft underline; inactive is muted. The Private tab gets a
 * lock glyph so the segregation is unambiguous at a glance.
 */
function TabStrip({
  tab,
  onChange,
}: {
  tab: Tab;
  onChange: (next: Tab) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Library shelves"
      className="flex items-center gap-1 border-b border-line"
    >
      <TabButton active={tab === "public"} onClick={() => onChange("public")}>
        Library
      </TabButton>
      <TabButton
        active={tab === "private"}
        onClick={() => onChange("private")}
      >
        <svg
          aria-hidden
          viewBox="0 0 16 16"
          className="h-3.5 w-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <rect x="3" y="7" width="10" height="6" rx="1" />
          <path d="M5 7V5a3 3 0 0 1 6 0v2" />
        </svg>
        Private
      </TabButton>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "focus-ring relative flex items-center gap-1.5 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] transition",
        active
          ? "text-accent after:absolute after:bottom-[-1px] after:left-3 after:right-3 after:h-[2px] after:bg-accent"
          : "text-foreground/55 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Genre + certification multi-select chip strip (issue #52).
 *
 * Each row is a "no-chips-selected = unfiltered" multi-select; click to
 * toggle. Selected chips render with the accent tone, unselected stay
 * neutral so the eye lands on the active filters first. Filtering is
 * client-side over the loaded library — we don't refetch on toggle.
 */
function FilterStrip({
  allGenres,
  allCerts,
  genreFilters,
  certFilters,
  onToggleGenre,
  onToggleCert,
}: {
  allGenres: string[];
  allCerts: string[];
  genreFilters: Set<string>;
  certFilters: Set<string>;
  onToggleGenre: (g: string) => void;
  onToggleCert: (c: string) => void;
}) {
  return (
    <div className="space-y-3 rounded-md border border-line bg-surface/30 px-3 py-3">
      {allGenres.length > 0 && (
        <FilterRow
          label="Genres"
          values={allGenres}
          selected={genreFilters}
          onToggle={onToggleGenre}
        />
      )}
      {allCerts.length > 0 && (
        <FilterRow
          label="Certification"
          values={allCerts}
          selected={certFilters}
          onToggle={onToggleCert}
        />
      )}
    </div>
  );
}

function FilterRow({
  label,
  values,
  selected,
  onToggle,
}: {
  label: string;
  values: string[];
  selected: Set<string>;
  onToggle: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/55">
        {label}
      </span>
      {values.map((v) => {
        const active = selected.has(v);
        return (
          <button
            key={v}
            type="button"
            onClick={() => onToggle(v)}
            aria-pressed={active}
            className={
              active
                ? "rounded-full border border-accent bg-accent/15 px-2.5 py-0.5 font-mono text-[11px] text-accent transition"
                : "rounded-full border border-line bg-surface/40 px-2.5 py-0.5 font-mono text-[11px] text-foreground/70 transition hover:border-line-strong"
            }
          >
            {v}
          </button>
        );
      })}
    </div>
  );
}

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to="/movies/$id"
      params={{ id: String(movie.id) }}
      className="group relative overflow-hidden rounded-md border border-line bg-surface/30 transition hover:border-line-strong"
    >
      <div className="relative">
        {movie.poster_url ? (
          <img
            src={movie.poster_url}
            alt=""
            className="aspect-[2/3] w-full object-cover transition group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          <div className="grid aspect-[2/3] place-items-center bg-surface-2 text-xs text-foreground/35">
            no poster
          </div>
        )}
        {/* Status badge floats over the poster top-right */}
        <div className="absolute right-2 top-2">
          {movie.has_file ? (
            <Status variant="chip" tone="good">
              downloaded
            </Status>
          ) : movie.monitored ? (
            <Status variant="chip" tone="accent">
              searching
            </Status>
          ) : (
            <Status variant="chip" tone="neutral">
              off
            </Status>
          )}
        </div>
        {/* Gradient at the bottom for the title */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background via-background/80 to-transparent"
        />
      </div>
      <div className="p-3">
        <div className="line-clamp-1 font-display text-sm font-medium leading-tight tracking-display-tight">
          {movie.title}
        </div>
        <div className="mt-1 flex items-center justify-between font-mono text-[11px] text-foreground/55">
          <span className="tabular-nums">{movie.year ?? "—"}</span>
          {movie.runtime_minutes && (
            <span className="tabular-nums">{movie.runtime_minutes}m</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function AddMovieModal({
  open,
  onClose,
  defaultPrivate = false,
}: {
  open: boolean;
  onClose: () => void;
  /** Migration 0062: when the user opens the modal from the Private
   *  tab, default the "Add as private" toggle to checked so the obvious
   *  next-step doesn't require an extra click. The user can still flip
   *  it before submitting. */
  defaultPrivate?: boolean;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<MovieLookupResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<MovieLookupResult | null>(null);

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const hits = await moviesApi.lookup(q);
      setResults(hits);
      setSelected(null);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Lookup failed", "error");
    } finally {
      setSearching(false);
    }
  };

  const reset = () => {
    setSelected(null);
    setQ("");
    setResults([]);
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      eyebrow="From TMDB"
      title="Add movie"
      width="lg"
    >
      <div className="space-y-4">
        {!selected && (
          <Field label="Search">
            <div className="flex gap-2">
              <TextInput
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="The Matrix"
              />
              <Button type="button" onClick={search} loading={searching}>
                Search
              </Button>
            </div>
          </Field>
        )}

        {selected ? (
          <AddMoviePicker
            hit={selected}
            defaultPrivate={defaultPrivate}
            onCancel={() => setSelected(null)}
            onAdded={() => {
              qc.invalidateQueries({ queryKey: ["movies"] });
              qc.invalidateQueries({ queryKey: ["collections"] });
              onClose();
              reset();
            }}
          />
        ) : results.length > 0 ? (
          <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
            {results.map((r) => (
              <div
                key={r.external_ids.tmdb}
                className="flex items-center gap-3 rounded-md border border-line bg-surface/40 p-2.5 transition hover:border-line-strong"
              >
                {r.poster_url ? (
                  <img
                    src={r.poster_url}
                    alt=""
                    className="h-20 w-14 shrink-0 rounded object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="grid h-20 w-14 shrink-0 place-items-center rounded bg-surface-2 text-[10px] text-foreground/35">
                    no poster
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-display text-base font-medium tracking-display-tight">
                    {r.title}
                  </div>
                  <div className="mt-0.5 font-mono text-xs text-foreground/55">
                    {r.year ?? "—"}
                  </div>
                  {r.overview && (
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground/70">
                      {r.overview}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  disabled={!r.external_ids.tmdb}
                  onClick={() => {
                    if (!r.external_ids.tmdb) return;
                    setSelected(r);
                  }}
                >
                  Pick
                </Button>
              </div>
            ))}
          </div>
        ) : (
          !searching &&
          q && (
            <p className="text-sm text-foreground/55">
              No results — try a different query.
            </p>
          )
        )}
      </div>
    </Modal>
  );
}

/**
 * Two-step add (issue #49): once a search result is picked, hit the
 * lookup-detail endpoint to learn whether the movie has a
 * `belongs_to_collection`. When it does, expose a "Monitor entire
 * collection" checkbox below the existing add controls. On submit, add
 * the movie first, then (if checked) flip the collection's `monitored`
 * flag — explicit "Add all parts" is a separate action on the
 * collections page.
 */
function AddMoviePicker({
  hit,
  defaultPrivate,
  onCancel,
  onAdded,
}: {
  hit: MovieLookupResult;
  defaultPrivate: boolean;
  onCancel: () => void;
  onAdded: () => void;
}) {
  const { toast } = useToast();
  const [monitorCollection, setMonitorCollection] = useState(false);
  const [addPrivate, setAddPrivate] = useState(defaultPrivate);
  const tmdbId = hit.external_ids.tmdb;

  const { data: detail, isLoading } = useQuery({
    queryKey: ["movie-lookup-detail", tmdbId],
    queryFn: () => moviesApi.lookupDetail(tmdbId as number),
    enabled: !!tmdbId,
  });

  const addMut = useMutation({
    mutationFn: async () => {
      if (!tmdbId) throw new Error("missing tmdb id");
      const movie = await moviesApi.add({
        tmdb_id: tmdbId,
        monitored: true,
        private: addPrivate,
      });
      if (
        monitorCollection &&
        detail?.belongs_to_collection?.tmdb_id != null
      ) {
        await moviesApi.collections.monitor(
          detail.belongs_to_collection.tmdb_id,
          true,
        );
      }
      return movie;
    },
    onSuccess: (m) => {
      const tags: string[] = [];
      if (addPrivate) tags.push("private");
      if (monitorCollection) tags.push("collection monitored");
      const suffix = tags.length > 0 ? ` (${tags.join(", ")})` : "";
      toast(`Added ${m.title}${suffix}`, "success");
      onAdded();
    },
    onError: (e) => {
      toast(e instanceof Error ? e.message : "Add failed", "error");
    },
  });

  return (
    <div className="space-y-4 rounded-md border border-line bg-surface/40 p-3">
      <div className="flex items-center gap-3">
        {hit.poster_url ? (
          <img
            src={hit.poster_url}
            alt=""
            className="h-24 w-16 shrink-0 rounded object-cover"
          />
        ) : (
          <div className="grid h-24 w-16 shrink-0 place-items-center rounded bg-surface-2 text-[10px] text-foreground/35">
            no poster
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-base font-medium tracking-display-tight">
            {hit.title}
          </div>
          <div className="mt-0.5 font-mono text-xs text-foreground/55">
            {hit.year ?? "—"}
          </div>
          {hit.overview && (
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground/70">
              {hit.overview}
            </p>
          )}
        </div>
      </div>

      {isLoading && (
        <p className="font-mono text-xs text-foreground/55">
          checking for collection…
        </p>
      )}

      {detail?.belongs_to_collection && (
        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-line bg-surface-2/40 p-2.5 hover:border-line-strong">
          <input
            type="checkbox"
            checked={monitorCollection}
            onChange={(e) => setMonitorCollection(e.target.checked)}
            className="mt-0.5 h-4 w-4 cursor-pointer accent-accent"
          />
          <div className="min-w-0 flex-1">
            <div className="font-medium">Monitor entire collection</div>
            <div className="mt-0.5 text-xs text-foreground/65">
              Auto-add new entries from{" "}
              <span className="font-medium text-foreground/85">
                {detail.belongs_to_collection.name}
              </span>{" "}
              as TMDB announces them. Today's parts aren't bulk-added —
              use “Add all” on the collection page for that.
            </div>
          </div>
        </label>
      )}

      {/* Migration 0062: per-add "private" toggle. Always shown so the
          user can mix-and-match without leaving the public Library tab.
          Default tracks the tab the modal was opened from. */}
      <label className="flex cursor-pointer items-start gap-2 rounded-md border border-line bg-surface-2/40 p-2.5 hover:border-line-strong">
        <input
          type="checkbox"
          checked={addPrivate}
          onChange={(e) => setAddPrivate(e.target.checked)}
          className="mt-0.5 h-4 w-4 cursor-pointer accent-accent"
        />
        <div className="min-w-0 flex-1">
          <div className="font-medium">Add as private</div>
          <div className="mt-0.5 text-xs text-foreground/65">
            File lands at the configured private library root and the
            row appears only on the Private tab — your household media
            server's container isn't mounted at that root, so it never
            sees the title.
          </div>
        </div>
      </label>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Back
        </Button>
        <Button onClick={() => addMut.mutate()} loading={addMut.isPending}>
          Add movie
        </Button>
      </div>
    </div>
  );
}
